import app from 'flarum/forum/app';
import Modal from 'flarum/common/components/Modal';
import Button from 'flarum/common/components/Button';
import Select from 'flarum/common/components/Select';
import extractText from 'flarum/common/utils/extractText';
import SpeciesSearchWidget from './SpeciesSearchWidget';
import { speciesNameWithSynonym } from '../utils/speciesName';
import { resolveHighertaxon, allowsMultipleSpecies } from '../utils/taxonGroups';
const RANKS_WITH_UNCERTAIN_SPECIES = ['Espèce', 'Sous-espèce', 'Forme', 'Variété'];
const RANKS_USING_OWN_LABEL = ['Famille', 'Ordre'];

// Same two gbif_id sentinels as PHP's Species::UNDETERMINED_ID/NO_SPECIES_ID
// (see that class's own docblock) - kept as local named constants rather
// than a shared JS module, since this is the only place in the JS codebase
// that submits either value.
const UNDETERMINED_GBIF_ID = 0;
const NO_SPECIES_GBIF_ID = 1000000000;
function confidenceOptions(rank) {
  const t = function (key, params) {
    if (params === void 0) {
      params = {};
    }
    return extractText(app.translator.trans(`champis-species.forum.assign_species.${key}`, params));
  };
  const options = [{
    cfId: 0,
    label: t('confidence_certain')
  }];
  if (rank && RANKS_WITH_UNCERTAIN_SPECIES.includes(rank)) {
    options.push({
      cfId: 1,
      label: t('confidence_uncertain_species')
    });
  }
  options.push({
    cfId: 2,
    label: rank && RANKS_USING_OWN_LABEL.includes(rank) ? t('confidence_uncertain_rank', {
      rank
    }) : t('confidence_uncertain_genus')
  });
  options.push({
    cfId: 3,
    label: t('confidence_broad')
  });
  return options;
}

/**
 * The same, for core's Select - a plain <select class="FormControl"> looks
 * just like the search field above it, Select adds the drop-down arrow.
 * Ascending cfIds, so the object keeps confidenceOptions()' order.
 */
function confidenceSelectOptions(rank) {
  return Object.fromEntries(confidenceOptions(rank).map(o => [String(o.cfId), o.label]));
}
/**
 * Assigns (or clears) the species linked to a discussion - opened either
 * from the composer (attrs.tags + attrs.onsubmit, discussion doesn't exist
 * yet) or from DiscussionControls on an existing discussion (attrs.discussion
 * + attrs.onsubmit). attrs.onsubmit(entries) receives a plain
 * [{gbifId, cfId}, ...] array; it may return a Promise (e.g. discussion.save())
 * or nothing (composer just stores it for the pending create request).
 */
export default class SpeciesAssignModal extends Modal {
  mode = 'species';
  entries = [];
  pendingSpecies = null;
  pendingCfId = 0;
  errorMessage = null;
  allowMultiple = false;
  loading = false;
  oninit(vnode) {
    super.oninit(vnode);
    const discussion = this.attrs.discussion;
    const tags = discussion ? discussion.tags() : this.attrs.tags || [];
    this.mode = discussion && discussion.speciesAssignmentMode() || 'species';
    this.entries = discussion ? discussion.species().map(s => ({
      ...s
    })) : this.attrs.initialEntries || [];
    this.pendingSpecies = null;
    this.pendingCfId = 0;
    this.errorMessage = null;
    this.highertaxon = resolveHighertaxon(tags);
    this.allowMultiple = allowsMultipleSpecies(tags);
  }
  className() {
    return 'Modal--medium SpeciesAssignModal';
  }
  title() {
    return app.translator.trans('champis-species.forum.assign_species.modal_title');
  }
  content() {
    const canManageSpecies = app.forum.attribute('canManageSpecies');
    const t = function (key, params) {
      if (params === void 0) {
        params = {};
      }
      return app.translator.trans(`champis-species.forum.assign_species.${key}`, params);
    };
    return m("div", {
      className: "Modal-body"
    }, this.errorMessage && m("div", {
      className: "SpeciesAssignModal-error"
    }, this.errorMessage), m("div", {
      className: "SpeciesAssignModal-modes"
    }, this.modeOption('species', t('mode_species')), this.modeOption('undetermined', t('mode_undetermined')), canManageSpecies && this.modeOption('no-species', t('mode_no_species'))), this.mode === 'species' && this.speciesModeContent(), m("div", {
      className: "SpeciesAssignModal-footer"
    }, m(Button, {
      className: "Button Button--primary",
      onclick: () => this.submit(),
      loading: this.loading,
      disabled: !this.canSubmit()
    }, t('save_button'))));
  }
  modeOption(value, label) {
    return m("label", {
      className: "SpeciesAssignModal-mode"
    }, m("input", {
      type: "radio",
      name: "species-mode",
      checked: this.mode === value,
      onchange: () => {
        this.mode = value;
      }
    }), ' ', label);
  }
  speciesModeContent() {
    const t = function (key, params) {
      if (params === void 0) {
        params = {};
      }
      return app.translator.trans(`champis-species.forum.assign_species.${key}`, params);
    };
    if (!this.allowMultiple) return this.singleSpeciesContent(t);
    return m("div", {
      className: "SpeciesAssignModal-species"
    }, this.entries.length > 0 && m("ul", {
      className: "SpeciesAssignModal-chips"
    }, this.entries.map((entry, index) => this.entryChip(entry, index, t))), this.pendingSpecies ? this.confidencePicker() : [this.suggestions(species => this.pickPending(species)), m(SpeciesSearchWidget, {
      highertaxon: this.highertaxon,
      label: t('search_label'),
      placeholder: t('search_placeholder'),
      onselect: species => this.pickPending(species)
    })], m("p", {
      className: "SpeciesAssignModal-hint"
    }, t('multi_species_hint')));
  }

  /**
   * Single-species mode (!allowMultiple): unlike the multi-species flow
   * above, there's no "pick from search -> review confidence -> click
   * Ajouter" two-step - picking a search result assigns it right away
   * (selectSingleSpecies()), and the search field + confidence select stay
   * visible even once an entry already exists, so replacing an existing
   * assignment is just "search again", not "remove chip, then search".
   */
  singleSpeciesContent(t) {
    const current = this.entries[0] || null;
    return m("div", {
      className: "SpeciesAssignModal-species"
    }, current && m("ul", {
      className: "SpeciesAssignModal-chips"
    }, this.entryChip(current, 0, t)), this.suggestions(species => this.selectSingleSpecies(species, 0)), m(SpeciesSearchWidget, {
      highertaxon: this.highertaxon,
      label: t('search_label'),
      placeholder: t('search_placeholder'),
      onselect: species => this.selectSingleSpecies(species, 0)
    }), current && m("div", {
      className: "SpeciesAssignModal-confidence"
    }, m("label", {
      for: "SpeciesAssignModal-confidence"
    }, t('confidence_label')), m(Select, {
      id: "SpeciesAssignModal-confidence",
      options: confidenceSelectOptions(current.rank),
      value: String(current.cfId),
      onchange: value => this.selectSingleSpecies(current, parseInt(value, 10))
    })));
  }

  /**
   * Above the search field: champis-identifier's "Suggérer une espèce
   * d'après les photos", which overrides this (see its
   * addSuggestionButtons.tsx) - a pick goes exactly where a search pick
   * would. Nothing without that extension.
   */
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  suggestions(onselect) {
    return null;
  }
  pickPending(species) {
    this.pendingSpecies = species;
    this.pendingCfId = 0;
    this.resolvePendingSpecies(species, 0);
  }
  entryChip(entry, index, t) {
    return m("li", {
      key: index,
      className: "SpeciesAssignModal-chip"
    }, m("span", {
      className: "SpeciesAssignModal-chipName"
    }, speciesNameWithSynonym(entry)), m("a", {
      className: "SpeciesAssignModal-chipRemove",
      "aria-label": extractText(t('remove_button')),
      title: extractText(t('remove_button')),
      onclick: () => this.removeEntry(index)
    }, m("i", {
      className: "icon fas fa-times",
      "aria-hidden": "true"
    })));
  }
  confidencePicker() {
    const t = key => app.translator.trans(`champis-species.forum.assign_species.${key}`);
    const pendingSpecies = this.pendingSpecies;
    const options = confidenceSelectOptions(pendingSpecies.rank);
    return m("div", {
      className: "SpeciesAssignModal-confidence"
    }, m("div", {
      className: "SpeciesAssignModal-pendingSpecies"
    }, speciesNameWithSynonym(pendingSpecies)), m("label", {
      for: "SpeciesAssignModal-pendingConfidence"
    }, t('confidence_label')), m(Select, {
      id: "SpeciesAssignModal-pendingConfidence",
      options: options,
      value: String(this.pendingCfId),
      onchange: value => {
        const cfId = parseInt(value, 10);
        this.pendingCfId = cfId;
        // The name itself changes with the confidence level (cf./sp./
        // s.l. markers - see resolvePendingSpecies()'s own docblock),
        // not just which option is picked in this dropdown - re-resolve
        // so the name shown above updates live to match, instead of
        // only catching up once actually added (or not at all, if
        // never re-fetched for the chosen cfId).
        this.resolvePendingSpecies(pendingSpecies, cfId);
      }
    }), m("div", {
      className: "SpeciesAssignModal-confidenceActions"
    }, m(Button, {
      className: "Button Button--primary",
      onclick: () => this.confirmAdd()
    }, t('add_button')), m(Button, {
      className: "Button Button--link",
      onclick: () => {
        this.pendingSpecies = null;
      }
    }, t('cancel_button'))));
  }

  /**
   * SpeciesSearchWidget's own results come straight from GBIF's species/
   * suggest (via /species-autocomplete - see that resource's own fields(),
   * no synonymCanonicalName/synonymAuthor at all) - picking a search hit
   * that's itself a synonym shows only that synonym's own bare name, with
   * no indication of what it actually resolves to, unlike every other
   * species display on the site (SpeciesLabel's "accepted (= synonym)"
   * format, driven by SpeciesLookupService::getSpeciesByIdCf() resolving
   * TopicSpecies.gbif_id at *display* time - the exact same resolution a
   * freshly-picked synonym will go through once saved). Refetches through
   * the public /species/{id} endpoint (used by species pages, same
   * service) to show precisely that resolved pairing before the user even
   * clicks "Ajouter", rather than after a save+reload reveals it was a
   * synonym all along.
   *
   * $cfId matters here, not just at submit time: SpeciesCorrectionLookup::
   * addCfWithNames() bakes the confidence level straight into canonicalName
   * itself (confirmed: the same gbif_id comes back "Tubaria hiemalis",
   * "Tubaria cf. hiemalis", "cf. Tubaria hiemalis" or "Tubaria hiemalis
   * s.l." for cfId 0/1/2/3 respectively, author unchanged) - so this is
   * re-called on every confidence-dropdown change too (see confidencePicker()),
   * not just once when a species is first picked, otherwise the name shown
   * here would silently keep whatever marker cfId 0 produced regardless of
   * which option actually ends up selected.
   *
   * Only ever enriches the *display* fields (canonicalName/author/
   * the synonym-prefixed fields/rank/status/...) - `id` is deliberately left untouched
   * (nothing in the /species/{id} response overwrites it, since `id` isn't
   * part of its own fields() schema), so confirmAdd()'s own `gbifId:
   * this.pendingSpecies.id` still submits whatever was actually picked
   * (accepted or synonym), matching how already-migrated/assigned species
   * behave - resolving to the accepted id *here* would permanently lose
   * the "originally identified as this synonym" distinction the whole
   * point of this fix is to surface, not erase.
   */
  resolvePendingSpecies(species, cfId) {
    app.request({
      method: 'GET',
      url: `${app.forum.attribute('apiUrl')}/species/${species.id}`,
      params: {
        cfId
      }
    }).then(response => {
      // Stale - the user picked something else, or changed the
      // confidence level again, while this was in flight.
      if (!this.pendingSpecies || this.pendingSpecies.id !== species.id || this.pendingCfId !== cfId) return;
      this.pendingSpecies = {
        ...this.pendingSpecies,
        ...response.data.attributes
      };
      m.redraw();
    }).catch(() => {
      // Left as the bare autocomplete result - still submittable, just
      // without the synonym annotation (same as before this existed).
    });
  }
  confirmAdd() {
    const pendingSpecies = this.pendingSpecies;
    const entry = {
      ...pendingSpecies,
      gbifId: Number(pendingSpecies.id),
      cfId: this.pendingCfId
    };
    this.entries = [...this.entries, entry];
    this.pendingSpecies = null;
    this.pendingCfId = 0;
  }

  /**
   * Single-species equivalent of confirmAdd() + resolvePendingSpecies():
   * no pendingSpecies staging step, the selection becomes the (sole) entry
   * immediately, then gets the same synonym-resolution enrichment in place
   * once it comes back. this._singleSpeciesToken guards against a stale
   * response overwriting a newer selection made while the request was in
   * flight (picking another species, or changing the confidence select).
   *
   * `species` is either a fresh SpeciesSearchWidget pick (has `id`, no
   * `gbifId`) or the already-assigned entry itself, passed back in by the
   * confidence <select> (has `gbifId` from DiscussionResource's own
   * `species` field - see extend.php - but no `id`, since that field never
   * sends one). Resolving `id` from either covers both.
   */
  _singleSpeciesToken = 0;
  selectSingleSpecies(species, cfId) {
    const id = 'id' in species && species.id !== undefined ? species.id : species.gbifId;
    const token = this._singleSpeciesToken = (this._singleSpeciesToken || 0) + 1;
    this.entries = [{
      ...species,
      gbifId: Number(id),
      cfId
    }];
    app.request({
      method: 'GET',
      url: `${app.forum.attribute('apiUrl')}/species/${id}`,
      params: {
        cfId
      }
    }).then(response => {
      if (this._singleSpeciesToken !== token) return;
      this.entries = [{
        ...this.entries[0],
        ...response.data.attributes
      }];
      m.redraw();
    }).catch(() => {});
  }
  removeEntry(index) {
    this.entries = this.entries.filter((_, i) => i !== index);
  }

  /**
   * No entries.length check for 'species' mode: submitting an empty array
   * is a legitimate save, not an accidental one - the server (extend.php's
   * species field set()) just deletes every TopicSpecies row for the
   * discussion, which is exactly "no species assigned at all" (see
   * speciesAssignmentMode()'s own getter - null once there are no rows
   * left), the outcome removing a chip and saving is meant to produce.
   */
  canSubmit() {
    return !this.loading;
  }
  submit() {
    this.loading = true;
    this.errorMessage = null;
    m.redraw();
    let payload;
    if (this.mode === 'undetermined') {
      payload = [{
        gbifId: UNDETERMINED_GBIF_ID,
        cfId: 0
      }];
    } else if (this.mode === 'no-species') {
      payload = [{
        gbifId: NO_SPECIES_GBIF_ID,
        cfId: 0
      }];
    } else {
      payload = this.entries.map(e => ({
        gbifId: e.gbifId,
        cfId: e.cfId
      }));
    }
    const result = this.attrs.onsubmit(payload);
    if (result && typeof result.then === 'function') {
      result.then(() => {
        // discussion.save()'s own success handler has already pushed the
        // freshly-resolved species into the store by the time this runs
        // (Model.save() resolves *after* pushPayload()) - redraw right
        // away rather than leaving it to hide()'s own animateHide() ->
        // (CSS transition) -> close() -> m.redraw() chain, the only
        // thing that otherwise ever triggers one on this path (unlike
        // the error branch below, which already redraws explicitly).
        // That chain does eventually redraw too, but only once the
        // modal's own closing animation finishes - this makes the
        // discussion (hero, list, wherever else renders `species`)
        // reflect the change the moment it's actually saved, not a beat
        // later once the modal is done animating away.
        m.redraw();
        this.hide();
      }, error => {
        this.loading = false;
        this.errorMessage = error && error.message || app.translator.trans('champis-species.forum.assign_species.error');
        m.redraw();
      });
    } else {
      this.hide();
    }
  }
}
flarum.reg.add('champis-species', 'forum/components/SpeciesAssignModal', SpeciesAssignModal);